import React, { useCallback, useMemo, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { Link, useLocation } from 'react-router-dom';
import moment from 'moment';
import dayjs from 'dayjs';
import { useLocationState } from '../../../Common/Util/CustomHooks';
import * as request from '../../../Common/Util/Request';
import * as excel from '../../../Common/Util/Excel';
import * as Button from '../../../Common/Component/Button';
import Table from '../../../Common/Component/Table';
import './index.scss';
import Swal from 'sweetalert2';
import { createTheme, Input, MenuItem, Select, ThemeProvider } from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import SearchIcon from '@mui/icons-material/Search';

function UserList(props) {
  const intl = useIntl();
  const location = useLocation();

  const isForeign = useMemo(() => location.pathname === '/user/foreign-list', [location.pathname]);

  const [locationState, setLocationState] = useLocationState({
    start_date: null,
    end_date: null,
    searchOption: 'name',
    searchText: '',
    filters: [],
  });

  const { start_date, end_date, filters } = locationState;
  const [searchOption, setSearchOption] = useState(locationState.searchOption);
  const [searchText, setSearchText] = useState(locationState.searchText);
  const [isDownloading, setIsDownloading] = useState(false);

  const onChangeSearchOption = useCallback((e) => {
    setSearchText('');
    setSearchOption(e.target.value);
  }, []);

  const onClickSearch = useCallback(() => {
    setLocationState({
      searchOption,
      searchText,
      filters: searchText ? [{ id: searchOption, value: searchText }] : [],
    });
  }, [setLocationState, searchOption, searchText]);

  const [users, setUsers] = useState([]);
  const [total, setTotal] = useState(0);

  const columns = useMemo(
    () => [
      { Header: 'ID', accessor: 'id' },
      { Header: '닉네임', accessor: 'nickname' },
      { Header: '이름', accessor: 'name' },
      {
        Header: '구분',
        accessor: 'role',
        Cell: ({
          row: {
            original: { permmission_level: permmissionLevel },
          },
          value,
        }) => {
          const role = value.toUpperCase();
          if (role === 'USER' && permmissionLevel) {
            return <FormattedMessage id={`ID_ROLE_${role}_${permmissionLevel}`} />;
          } else {
            return <FormattedMessage id={`ID_ROLE_${role}`} />;
          }
        },
      },
      { Header: '총 구매 강좌수', accessor: 'countAllPaidClass' },
      {
        Header: '총 결제 금액',
        accessor: 'amountAllPaidClass',
        Cell: ({ value }) => value.toPrice(),
      },
      { Header: '총 방문수', accessor: 'count_visit' },
      {
        Header: '가입일',
        accessor: 'createdAt',
        Cell: ({ value: createdAt }) => (createdAt ? moment.utc(createdAt).format('YYYY-MM-DD') : '-'),
      },
      {
        Header: '최종방문일',
        accessor: 'lastLoginAt',
        Cell: ({ value: lastLoginAt }) => (lastLoginAt ? moment.utc(lastLoginAt).format('YYYY-MM-DD') : '-'),
      },
      {
        Header: '가입 유형',
        accessor: 'login_type',
      },
      {
        Header: '탈퇴',
        accessor: 'deletedAt',
        Cell: ({ value: deletedAt }) => (deletedAt ? '탈퇴' : '가입'),
      },
      {
        Header: '정보수정',
        accessor: (row) => (
          <Link
            to={{
              pathname: `${location.pathname}/${row.id}`,
              state: row,
            }}
          >
            수정
          </Link>
        ),
      },
    ],
    [location.pathname],
  );

  const loadUsers = useCallback(
    ({ page, limit }) => {
      const params = {
        page,
        limit,
        korean: !isForeign,
        filtered: JSON.stringify(filters),
      };

      if (start_date) {
        params.start_date = moment(start_date).format('YYYY-MM-DD');
      }
      if (end_date) {
        params.end_date = moment(end_date).format('YYYY-MM-DD');
      }

      return request.users(params);
    },
    [isForeign, filters, start_date, end_date],
  );

  const fetchData = useCallback(
    ({ pageIndex, pageSize }) => {
      loadUsers({
        page: pageIndex + 1,
        limit: pageSize,
      }).then(({ users }) => {
        setUsers(users.rows);
        setTotal(users.count);
      });
    },
    [loadUsers],
  );

  const onClickCSV = useCallback(() => {
    const startDateMoment = start_date ? moment(start_date) : moment('2020-01-01');
    const endDateMoment = end_date ? moment(end_date) : moment();
    if (endDateMoment.diff(startDateMoment, 'days') > 365) {
      Swal.fire({
        title: '기간 설정 오류',
        text: '다운로드 가능한 기간은 최대 1년입니다. 기간을 다시 설정해주세요.',
        icon: 'error',
      });
      return;
    }

    setIsDownloading(true);
    loadUsers({ page: 1, limit: 99999 })
      .then(({ users }) => {
        const data = users.rows.map((user) => ({
          ID: user.id,
          닉네임: user.nickname,
          이름: user.name,
          이메일: user.email,
          핸드폰번호: user.phone ? user.phone.toPhone() : '-',
          국가정보: user.FK_country?.name_kor || '-',
          '혜택 수신 동의': user.is_marketing_on ? '동의' : '비동의',
          구분: intl.formatMessage({ id: `ID_ROLE_${user.role.toUpperCase()}_${user.permmission_level}` }),
          '총 구매 강좌수': user.countAllPaidClass,
          '총 결제 금액': user.amountAllPaidClass,
          '총 방문수': user.count_visit,
          '관심 기업': user.interestedCompanies?.join(', ') || '-',
          '기타 관심 기업': user.otherInterestedCompanies?.join(', ') || '-',
          가입일: user.createdAt ? moment.utc(user.createdAt).format('YYYY-MM-DD') : '-',
          최종방문일: user.lastLoginAt ? moment.utc(user.lastLoginAt).format('YYYY-MM-DD') : '-',
          탈퇴일: user.deletedAt ? moment.utc(user.deletedAt).format('YYYY-MM-DD') : '-',
        }));
        excel.downloadXLSX(data);
        setIsDownloading(false);
      })
      .catch(() => {
        Swal.fire({
          icon: 'error',
          title: '다운로드 실패',
          text: '다운로드에 실패하였습니다. 다시 시도해주세요.',
        });
        setIsDownloading(false);
      });
  }, [intl, loadUsers, start_date, end_date]);

  const ejeTheme = createTheme({
    palette: {
      pink: {
        light: '#f46981',
        main: '#F24462',
        dark: '#a92f44',
        contrastText: '#fff',
      },
    },
  });

  return (
    <div className="user-list">
      <ThemeProvider theme={ejeTheme}>
        <div className="user-list__top">
          <div className="user-list__date">
            기간
            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <DatePicker
                value={start_date && dayjs(start_date)}
                maxDate={end_date && dayjs(end_date)}
                onChange={(value) => setLocationState({ start_date: dayjs(value).format('YYYY-MM-DD') })}
                label={start_date && 'From'}
                disableFuture
                views={['year', 'month', 'day']}
              />
              -
              <DatePicker
                value={end_date && dayjs(end_date)}
                minDate={start_date && dayjs(start_date)}
                onChange={(value) => setLocationState({ end_date: dayjs(value).format('YYYY-MM-DD') })}
                label={end_date && 'From'}
                disableFuture
                views={['year', 'month', 'day']}
              />
            </LocalizationProvider>
          </div>
          <div className="user-list__search">
            <Select value={searchOption} onChange={onChangeSearchOption} displayEmpty color="pink" size="small">
              <MenuItem value="name" sx={{ fontSize: '0.875rem' }}>
                이름
              </MenuItem>
              <MenuItem value="nickname" sx={{ fontSize: '0.875rem' }}>
                닉네임
              </MenuItem>
              <MenuItem value="email" sx={{ fontSize: '0.875rem' }}>
                이메일
              </MenuItem>
              <MenuItem value="phone" sx={{ fontSize: '0.875rem' }}>
                핸드폰 번호
              </MenuItem>
              <MenuItem value="id" sx={{ fontSize: '0.875rem' }}>
                회원ID
              </MenuItem>
            </Select>
            <Input
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') onClickSearch();
              }}
              color="pink"
              placeholder="검색어를 입력하세요."
            />
            <Button.MUI
              onClick={onClickSearch}
              variant="outlined"
              endIcon={<SearchIcon fontSize="small" />}
              color="pink"
              sx={{ height: '2rem' }}
            >
              검색
            </Button.MUI>
          </div>
        </div>
        <div className="user-list__total">
          총 회원수 {total}
          <button
            className={
              'user-list__excel--btn' +
              (filters.length < 1 && !start_date && !end_date ? ' user-list__excel__disabled--btn' : '')
            }
            style={{ cursor: isDownloading ? 'not-allowed' : 'pointer' }}
            onClick={() => {
              filters.length < 1 && !start_date && !end_date
                ? Swal.fire({
                    title: '기간 설정 또는 검색 후 다운로드 가능합니다.',
                    text: '좌측 상단에서 기간 설정 또는 검색 후 다운로드해 주세요.',
                    icon: 'info',
                    confirmButtonColor: '#F24462',
                  })
                : !isDownloading
                ? onClickCSV()
                : undefined;
            }}
          >
            {isDownloading ? 'Loading...' : '액셀 다운로드'}
          </button>
        </div>
        <Table data={users} columns={columns} total={total} pageSize={20} fetchData={fetchData} />
      </ThemeProvider>
    </div>
  );
}

export default UserList;
